import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Container,
  Paper,
  Link
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import authService from '../../services/authService';
import './AuthModal.css';

const AuthModal = ({ onLoginSuccess, onForgotPasswordClick }) => {
  const [tabValue, setTabValue] = useState(0); // 0 = Login, 1 = Register
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Register form state
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: ''
  });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    if (!loginData.email || !loginData.password) {
      setError('Email en wachtwoord zijn verplicht.');
      return;
    }

    if (!validateEmail(loginData.email)) {
      setError('Ongeldig emailadres.');
      return;
    }

    setLoading(true);
    const result = await authService.login(loginData.email, loginData.password);
    setLoading(false);

    if (result.success) {
      onLoginSuccess(result.account);
    } else {
      setError(result.message);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!registerData.firstName || !registerData.lastName || !registerData.email || !registerData.confirmEmail || !registerData.password || !registerData.confirmPassword) {
      setError('Alle velden zijn verplicht.');
      return;
    }

    if (registerData.firstName.trim().length === 0) {
      setError('Voornaam mag niet leeg zijn.');
      return;
    }

    if (registerData.lastName.trim().length === 0) {
      setError('Achternaam mag niet leeg zijn.');
      return;
    }

    if (!validateEmail(registerData.email)) {
      setError('Ongeldig emailadres formaat.');
      return;
    }

    if (registerData.email !== registerData.confirmEmail) {
      setError('De emailadressen komen niet overeen.');
      return;
    }

    if (registerData.password.length < 6) {
      setError('Het wachtwoord moet minimaal 6 tekens lang zijn.');
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      setError('De wachtwoorden komen niet overeen.');
      return;
    }

    setLoading(true);
    const result = await authService.register(
      registerData.firstName,
      registerData.lastName,
      registerData.email,
      registerData.password,
      registerData.confirmPassword
    );
    setLoading(false);

    if (result.success) {
      setError('');
      setTabValue(0); // Terug naar login
      setLoginData({ email: registerData.email, password: '' });
      setRegisterData({ firstName: '', lastName: '', email: '', confirmEmail: '', password: '', confirmPassword: '' });
      setError('Account aangemaakt! Je kunt nu inloggen.');
    } else {
      setError(result.message);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          sx={{
            padding: 4,
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <h1 style={{ margin: 0 }}>MissionTracker</h1>
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange} centered>
              <Tab label="Inloggen" />
              <Tab label="Account aanmaken" />
            </Tabs>
          </Box>

          {error && (
            <Alert 
              severity={error.includes('aangemaakt') ? 'success' : 'error'}
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          {/* Login Tab */}
          {tabValue === 0 && (
            <Box component="form" onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={loginData.email}
                onChange={handleLoginChange}
                margin="normal"
                variant="outlined"
                placeholder="jouw@email.com"
              />

              <TextField
                fullWidth
                label="Wachtwoord"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={loginData.password}
                onChange={handleLoginChange}
                margin="normal"
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Box sx={{ textAlign: 'right', mt: 1, mb: 2 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    onForgotPasswordClick?.();
                  }}
                  sx={{ cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  Wachtwoord vergeten?
                </Link>
              </Box>

              <Button
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
                sx={{ mt: 1, py: 1.5 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Inloggen'}
              </Button>
            </Box>
          )}

          {/* Register Tab */}
          {tabValue === 1 && (
            <Box component="form" onSubmit={handleRegister}>
              <TextField
                fullWidth
                label="Voornaam"
                name="firstName"
                value={registerData.firstName}
                onChange={handleRegisterChange}
                margin="normal"
                variant="outlined"
                placeholder="Jouw voornaam"
              />

              <TextField
                fullWidth
                label="Achternaam"
                name="lastName"
                value={registerData.lastName}
                onChange={handleRegisterChange}
                margin="normal"
                variant="outlined"
                placeholder="Jouw achternaam"
              />

              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={registerData.email}
                onChange={handleRegisterChange}
                margin="normal"
                variant="outlined"
                placeholder="jouw@email.com"
              />

              <TextField
                fullWidth
                label="Email herhalen"
                name="confirmEmail"
                type="email"
                value={registerData.confirmEmail}
                onChange={handleRegisterChange}
                margin="normal"
                variant="outlined"
                placeholder="Herhaal je emailadres"
              />

              <TextField
                fullWidth
                label="Wachtwoord"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={registerData.password}
                onChange={handleRegisterChange}
                margin="normal"
                variant="outlined"
                helperText="Minimaal 6 tekens"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                fullWidth
                label="Wachtwoord herhalen"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={registerData.confirmPassword}
                onChange={handleRegisterChange}
                margin="normal"
                variant="outlined"
                placeholder="Herhaal het wachtwoord"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleClickShowConfirmPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Button
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
                sx={{ mt: 3, py: 1.5 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Account aanmaken'}
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default AuthModal;
